import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, Typography, MenuItem, Box } from "@material-ui/core";
import { useForm } from "react-hook-form";
import AmoTextField from "components/inputs/AmoTextField";
import AmoModal from "components/AmoModal";
import { useSnackbar } from "contexts/SnackbarContext";
import { snackbarTypes } from "constants/snackbar";
import FileUploadList from "components/FileUploadList";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Skeleton from "@material-ui/lab/Skeleton";
import * as reviewService from "api/services/reviewsService";
import { fileTypes, fileAssociations } from "constants/fileTypes";
import colors from "constants/colors";
import { useIsMounted } from "hooks/useIsMounted";
import clsx from "clsx";
import { reconciliationReasonChange } from "../constants/reconciliationReasonChange";
import { Dollars, formatToCurrency } from "../utils/number";

// sets the Material-UI styles for this component
const useStyles = makeStyles((theme) => ({
  labelTest: {
    fontWeight: "bold",
  },
  inputWidth: {
    width: "100%",
  },
  fileErrorBox: {
    position: "relative",
    width: "100%",
    height: "2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "0.5rem",
  },
  errorText: {
    fontSize: "0.75rem",
    color: colors.red.main,
  },
  spacedBox: {
    "& > *:not(:last-child)": {
      marginBottom: theme.spacing(5),
    },
  },
}));

/**
 * @param {object} props - object containing props for this component
 * @param {boolean} props.showModal - controls visibility of the modal
 * @param {Function} props.closeModalFunction - method that triggers when the close button is clicked
 * @param {boolean} props.newReconciliation - indicates if this is a new certificate
 * @param {number} props.projectId - related project
 * @param {number} props.municipalityId - municipality number
 * @param {number} props.year - audited year
 * @param {number} props.originalAmount - original amount
 * @param {number} props.newAmount - new amount
 * @param {Function} props.onSaveHook - hook that is called after the save function is called
 * @param {number} props.projectCategory - sets the projectCategory related to the projects
 * @returns - the manage groupings modal, styled for the AMO project
 */
const ReviseExpendituresReconciliationModal = (props) => {
  const {
    showModal,
    closeModalFunction,
    municipalityId,
    projectId,
    originalAmount,
    newAmount,
    newReconciliation,
    year,
    projectCategory,
    onSaveHook,
  } = props;

  const theme = useTheme();
  const reviseExpendituresReconciliationSchema = yup
    .object({
      authority: yup
        .string()
        .max(100)
        .required("Authority is a required field"),
      statusReconciliation: yup.string().required(),
    })
    .required();

  const defaultValues = {
    authority: "",
    statusReconciliation: `${reconciliationReasonChange.amoStaffReview}`,
    id: "",
  };
  const classes = useStyles(props);

  const mounted = useIsMounted();

  const { control, reset, formState, handleSubmit } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(reviseExpendituresReconciliationSchema),
  });

  const { isValid, isSubmitting } = formState;

  const { showSnackbar } = useSnackbar();

  const [file, setFile] = useState(null);
  const [initialFile, setInitialFile] = useState(null);
  const [fileUploadAction, setFileUploadAction] = useState("Idle");

  const fileIsDirty = useMemo(() => file?.id !== initialFile?.id, [
    file,
    initialFile,
  ]);
  const description = `Fill out all details below to revise ${year}’s expenditures for project ${projectId}.`;

  const projectCategoryMap = {
    1: "Broadband Connectivity",
    2: "Brownfield Redevelopment",
    3: "Capacity-Building",
    4: "Community Energy Systems",
    5: "Culture",
    6: "Resilience",
    7: "Drinking Water",
    8: "Fire Stations",
    9: "Local Roads and Bridges",
    10: "Public Transit",
    11: "Recreation",
    12: "Regional and Local Airports",
    13: "Short-line Rail",
    14: "Short-sea Shipping",
    15: "Solid Waste",
    16: "Sports",
    17: "Tourism",
    18: "Wastewater",
  };

  const projectCategoryName =
    projectCategoryMap[projectCategory] || "Unknown Category";

  const amountChange = newAmount - originalAmount;
  const changeType = amountChange > 0 ? "increased" : "decreased";

  const descriptionForBackend = `${projectCategoryName} expenditures on project ${projectId} ${changeType}`;

  const saveReconciliation = async (formValues, fileId = null) => {
    const payload = {
      year,
      municipalityId,
      projectId,
      description: descriptionForBackend,
      projectCategory,
      amount: new Dollars(newAmount).minusValue(originalAmount),
      authority: formValues.authority,
      statusReconciliation: formValues.statusReconciliation,
    };
    try {
      if (file?.id !== initialFile?.id) {
        payload.fileId = fileId;
        await reviewService.postReconciliationChanges(payload);
      } else {
        payload.fileId = fileId;
        await reviewService.postReconciliationChanges(payload);
      }

      if (!mounted.current) {
        return;
      }

      const updateSucceeded = (await onSaveHook()) ?? true;
      if (!updateSucceeded) {
        throw new Error(
          "Project expenditure update failed after reconciliation save"
        );
      }

      showSnackbar("Reconciliation saved successfully", snackbarTypes.success);
      if (mounted.current) {
        onClose();
      }
    } catch (error) {
      console.error(error);

      if (!mounted.current) {
        return;
      }

      showSnackbar(
        "An error occurred while saving the reconciliation",
        snackbarTypes.error
      );
    }
  };

  const onCancel = () => {
    if (fileIsDirty && newReconciliation) {
      setFileUploadAction("Rollback");
    } else {
      onClose();
    }
  };

  const onClose = () => {
    if (mounted.current) {
      closeModalFunction();
      reset(defaultValues);
      setFile(null);
      setInitialFile(null);
    }
  };

  const onSave = (event) => {
    handleSubmit((formValues) => saveReconciliation(formValues, file?.id))(
      event
    );
  };

  const onFileRollback = () => {
    setFileUploadAction("Idle");
    onClose();
  };

  const textBlock = (text) => (
    <Grid item className={classes.contentPadding}>
      <Typography
        className={clsx(classes.fullWidth, classes.contentFont)}
        variant="body1"
        component="span"
      >
        {text}
      </Typography>
    </Grid>
  );

  return (
    <AmoModal
      data-testid="amoReviseExpendituresReconciliationModal"
      open={showModal}
      variant="default"
      title="Revise expenditures?"
      width="38rem"
      showButton
      buttonsLeftSpacer={1}
      closePopover={onCancel}
      buttons={[
        {
          key: "yes",
          label: "Yes",
          color: "primary",
          variant: "contained",
          width: "14rem",
          disabled: isSubmitting || !isValid,
          onClick: onSave,
        },
        {
          key: "no",
          label: "No",
          color: "secondary",
          variant: "outlined",
          width: "14rem",
          onClick: onCancel,
        },
      ]}
    >
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} />
        {textBlock(description)}
        <Box width={1} px={7} className={classes.spacedBox}>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={6}>
              <Typography className={classes.labelTest}>
                Original amount
              </Typography>
              <Typography>{formatToCurrency(originalAmount)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.labelTest}>New amount</Typography>
              <Typography>{formatToCurrency(newAmount)}</Typography>
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <AmoTextField
              label="Reason for change *"
              control={control}
              select
              id="reconciliationStatus"
              name="statusReconciliation"
              className={classes.inputWidth}
            >
              <MenuItem
                key="statusReconciliationNone"
                value={`${reconciliationReasonChange.amoStaffReview}`}
              >
                {reconciliationReasonChange.amoStaffReview}
              </MenuItem>
              <MenuItem
                key="statusReconciliationApproved"
                value={`${reconciliationReasonChange.annualReportingCompleted}`}
              >
                {reconciliationReasonChange.annualReportingCompleted}
              </MenuItem>
              <MenuItem
                key="statusReconciliationNotApproved"
                value={`${reconciliationReasonChange.revisionRequested}`}
              >
                {reconciliationReasonChange.revisionRequested}
              </MenuItem>
            </AmoTextField>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <AmoTextField
              multiline
              rows={1}
              rowsMax={10}
              control={control}
              label="Authority *"
              id="authority"
              name="authority"
              variant="outlined"
              placeholder="Authority"
              className={classes.inputWidth}
              focused={false}
            />
          </Grid>

          <Grid item container xs={12} spacing={2}>
            {newReconciliation ? (
              <>
                <FileUploadList
                  key={file ? file.id : "empty-file"}
                  fileYear={year}
                  fileAssociationId={fileAssociations.Reconciliation}
                  fileAssociationObjectId={year}
                  municipalityId={municipalityId}
                  showAllowedTypesHint
                  allowedTypes={[
                    fileTypes.docx,
                    fileTypes.msg,
                    fileTypes.pdf,
                    fileTypes.xlsx,
                  ]}
                  initialValue={file ? [file] : []}
                  onChange={(files) => {
                    setFile(files?.[0]);
                  }}
                  uploadAction={fileUploadAction}
                  onRollback={onFileRollback}
                  disableSizeLimit
                />
              </>
            ) : (
              <Skeleton />
            )}
          </Grid>
        </Box>
        <Box height={theme.spacing(60)} />
      </Grid>
    </AmoModal>
  );
};

ReviseExpendituresReconciliationModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  closeModalFunction: PropTypes.func.isRequired,
  municipalityId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  originalAmount: PropTypes.number.isRequired,
  newAmount: PropTypes.number.isRequired,
  newReconciliation: PropTypes.bool,
  year: PropTypes.number.isRequired,
  onSaveHook: PropTypes.func.isRequired,
  projectCategory: PropTypes.number,
};

ReviseExpendituresReconciliationModal.defaultProps = {
  newReconciliation: true,
  projectCategory: 0,
};

export default ReviseExpendituresReconciliationModal;
